import {
    Row,
    Col,
    Card,
    Typography,
    Empty,
    Pagination,
    Table,
    Modal,
    Select,
    DatePicker,
    Space,
    Input,
} from "antd";
import Loader from "../../../components/ui/loader";
import { UserOutlined } from "@ant-design/icons";
import { CiCircleChevLeft, CiCircleChevRight } from "react-icons/ci";
import { IoIosRefresh } from "react-icons/io";
import { ImSpinner2 } from "react-icons/im";
import { useIsMobile } from "../admin-dashboard/hooks/useIsMobile";
import { useAdminUsers } from "../admin-dashboard/hooks/useAdminUsers";

const { Option } = Select;
const { TextArea } = Input;

const UserManagement = () => {
    const isMobile = useIsMobile();
    const {
        userDetails,
        userCount,
        filteredUsers,
        sortedUsers,
        currentUserPage,
        userItemsPerPage,
        paginateUsers,
        userSearch,
        setUserSearch,
        userSortField,
        setUserSortField,
        userSortOrder,
        setUserSortOrder,
        isUserDataLoading,
        usersLoading,
        editUserLoading,
        userArchiveLoading,
        approvalLoading,
        rejectionLoading,
        exportLoading,
        refreshUsers,
        selectedUser,
        setSelectedUser,
        showEditModal,
        setShowEditModal,
        selectedEditField,
        setSelectedEditField,
        newValue,
        setNewValue,
        handleSaveChanges,
        showUserArchiveModal,
        setShowUserArchiveModal,
        archiveReason,
        setArchiveReason,
        handleArchiveUser,
        showApprovalModal,
        setShowApprovalModal,
        isRejecting,
        rejectionReason,
        setRejectionReason,
        handleModalCancel,
        handleRejectClick,
        handleInstructorApproval,
        handleRejectInstructor,
        showActivationModal,
        setShowActivationModal,
        handleUserActivation,
        handleUserDeactivation,
        filterModalVisible,
        setFilterModalVisible,
        startDate,
        setStartDate,
        endDate,
        setEndDate,
        exportToCSV,
        message,
    } = useAdminUsers();
    if (!userDetails && !isUserDataLoading) return null;

    const userManagementColumns = [
        { title: "User ID", dataIndex: "user_id", key: "user_id", className: "table-cell", sorter: (a, b) => a.user_id - b.user_id },
        { title: "First Name", dataIndex: "first_name", key: "first_name", className: "table-cell", sorter: (a, b) => a.first_name.localeCompare(b.first_name) },
        { title: "Last Name", dataIndex: "last_name", key: "last_name", className: "table-cell", sorter: (a, b) => a.last_name.localeCompare(b.last_name) },
        { title: "Email", dataIndex: "email", key: "email", className: "table-cell", sorter: (a, b) => a.email.localeCompare(b.email) },
        {
            title: "Role",
            key: "role",
            className: "table-cell",
            sorter: (a, b) => {
                const roleA = a.role === "instructor" && a.type === "admin" ? "Admin" : a.role;
                const roleB = b.role === "instructor" && b.type === "admin" ? "Admin" : b.role;
                return roleA.localeCompare(roleB);
            },
            render: (_, record) =>
                record.role === "Instructor" && record.type === "admin"
                    ? "Admin"
                    : record.role === "Instructor"
                        ? "Instructor"
                        : record.role,
        },
        { title: "Phone Number", dataIndex: "phone_number", key: "phone_number", className: "table-cell" },
        {
            title: "Creation Date",
            dataIndex: "creation_date",
            key: "creation_date",
            className: "table-cell",
            sorter: (a, b) => new Date(a.creation_date) - new Date(b.creation_date),
            render: (text) => new Date(text).toLocaleDateString(),
        },
        {
            title: "Approval Status",
            dataIndex: "is_approved",
            key: "is_approved",
            className: "table-cell",
            sorter: (a, b) => a.is_approved - b.is_approved,
            render: (text, record) =>
                record.role === "Instructor" ? (
                    <button
                        disabled={text === 1 || text === null}
                        onClick={() => {
                            setSelectedUser(record);
                            setShowApprovalModal(true);
                        }}
                        className={text === 1 || text === null ? "approved-badge" : text === 2 ? "rejected-badge" : "pending-badge"}
                    >
                        {text === 1 || text === null ? "Approved" : text === 2 ? "Rejected" : "Pending Approval"}
                    </button>
                ) : null,
        },
        {
            title: "Active Status",
            dataIndex: "status",
            key: "status",
            className: "table-cell",
            sorter: (a, b) => a.status - b.status,
            render: (text, record) => (
                <button
                    disabled={userDetails.user_id === record.user_id}
                    onClick={() => {
                        setSelectedUser(record);
                        setShowActivationModal(true);
                    }}
                    className={
                        text === true || text === null
                            ? userDetails.user_id === record.user_id
                                ? "pending-badge"
                                : "approved-badge"
                            : "rejected-badge"
                    }
                >
                    {text === true || text === null ? "Active" : "Inactive"}
                </button>
            ),
        },
        {
            title: "Actions",
            key: "actions",
            className: "table-cell",
            render: (_, record) => (
                <div style={{ display: "flex", gap: "8px" }}>
                    <button
                        className="btn btn-sm approve-btn"
                        onClick={() => {
                            setShowEditModal(true);
                            setSelectedUser(record);
                        }}
                    >
                        Edit
                    </button>
                    {/* <button
                        disabled
                        className="btn btn-sm danger-btn"
                        onClick={() => {
                            setShowUserArchiveModal(true);
                            setSelectedUser(record);
                        }}
                    >
                        Archive User
                    </button> */}
                </div>
            ),
        },
    ];

    return (
        <div>
            {message.text && (
                <div
                    className={`toast-alert ${message.visible ? "toast-show" : ""} ${message.type === "success" ? "toast-success" : "toast-error"
                        }`}
                    role="alert"
                >
                    {message.text}
                </div>
            )}

            {/* archive modal */}
            <Modal
                centered
                title="Archive User Account"
                open={showUserArchiveModal}
                onCancel={() => {
                    setShowUserArchiveModal(false);
                    setArchiveReason("");
                }}
                footer={[
                    <div key="footer" className="d-flex gap-1" style={{ justifyContent: "right" }}>
                        <button
                            onClick={() => {
                                setShowUserArchiveModal(false);
                                setArchiveReason("");
                            }}
                            className="custom-cancel-btn btn"
                        >
                            Cancel
                        </button>
                        <button className="custom-danger-btn btn" onClick={handleArchiveUser}>
                            {userArchiveLoading ? <ImSpinner2 className="button-spinner-white" /> : "Archive"}
                        </button>
                    </div>,
                ]}
            >
                <p><strong>User ID:</strong> {selectedUser?.user_id}</p>
                <p><strong>Name:</strong> {selectedUser?.first_name} {selectedUser?.last_name}</p>
                <p><strong>Email:</strong> {selectedUser?.email}</p>
                <input
                    className="form-control mb-3"
                    type="text"
                    placeholder="Enter reason for archiving..."
                    value={archiveReason}
                    onChange={(e) => setArchiveReason(e.target.value)}
                />
                <p>
                    Are you sure you want to archive this user's account? All their data including{" "}
                    <strong>owned courses</strong>, <strong>account information</strong> and{" "}
                    <strong>course progress</strong> will be{" "}
                    <span style={{ color: "#ff0000" }}>archived</span>.
                </p>
            </Modal>

            {/* approval / rejection modal */}
            <Modal
                centered
                title="Instructor Approval"
                open={showApprovalModal}
                onCancel={handleModalCancel}
                footer={
                    isRejecting
                        ? [
                            <div key="rejection-footer" className="d-flex gap-1" style={{ justifyContent: "right" }}>
                                <button
                                    className="custom-cancel-btn btn"
                                    onClick={handleModalCancel}
                                    disabled={rejectionLoading}
                                >
                                    Back
                                </button>
                                <button
                                    className="custom-danger-btn btn"
                                    onClick={handleRejectInstructor}
                                    disabled={rejectionLoading}
                                >
                                    {rejectionLoading ? <ImSpinner2 className="btn-spinner" /> : "Submit Rejection"}
                                </button>
                            </div>,
                        ]
                        : [
                            <div key="default-footer" className="d-flex gap-1" style={{ justifyContent: "right" }}>
                                <button onClick={handleInstructorApproval} className="custom-ok-btn btn" disabled={approvalLoading}>
                                    {approvalLoading ? <ImSpinner2 className="btn-spinner" /> : "Approve"}
                                </button>
                                <button className="custom-danger-btn btn" onClick={handleRejectClick}>
                                    Reject
                                </button>
                            </div>,
                        ]
                }
            >
                <p>Review this Instructor application and choose to approve or reject the request</p>

                {selectedUser && (
                    <div style={{ marginTop: "20px" }}>
                        <p><strong>User ID:</strong> {selectedUser.user_id}</p>
                        <p><strong>Name:</strong> {selectedUser.first_name} {selectedUser.last_name}</p>
                        <p><strong>Email:</strong> {selectedUser.email}</p>
                    </div>
                )}

                {isRejecting && (
                    <div style={{ marginTop: "16px" }}>
                        <label htmlFor="rejection-reason" style={{ display: "block", marginBottom: "6px" }}>
                            <strong>Rejection reason</strong>
                        </label>
                        <TextArea
                            id="rejection-reason"
                            rows={4}
                            placeholder="Provide a rejection reason..."
                            value={rejectionReason}
                            onChange={(e) => setRejectionReason(e.target.value)}
                        />
                    </div>
                )}
            </Modal>

            {/* activation modal */}
            <Modal
                centered
                title="User Activation"
                open={showActivationModal}
                onCancel={() => setShowActivationModal(false)}
                footer={[
                    <div key="footer" className="d-flex gap-1" style={{ justifyContent: "right" }}>
                        <button onClick={handleUserActivation} className="custom-ok-btn btn">
                            {approvalLoading ? <ImSpinner2 className="btn-spinner" /> : "Activate"}
                        </button>
                        <button className="custom-danger-btn btn" onClick={handleUserDeactivation}>
                            {rejectionLoading ? <ImSpinner2 className="btn-spinner" /> : "Deactivate"}
                        </button>
                    </div>,
                ]}
            >
                <p>Activate or deactivate the selected user account.</p>
                {selectedUser && (
                    <div style={{ marginTop: "20px" }}>
                        <p><strong>User ID:</strong> {selectedUser.user_id}</p>
                        <p><strong>Name:</strong> {selectedUser.first_name} {selectedUser.last_name}</p>
                        <p><strong>Email:</strong> {selectedUser.email}</p>
                    </div>
                )}
            </Modal>

            {/* edit user modal */}
            <Modal
                centered
                title={<div style={{ fontSize: "large" }} className="d-flex justify-content-center">Edit information</div>}
                open={showEditModal}
                onCancel={() => {
                    setShowEditModal(false);
                    setSelectedEditField("");
                    setNewValue("");
                }}
                footer={null}
            >
                {selectedUser && (
                    <>
                        <p style={{ fontSize: "medium" }}>
                            <strong>User:</strong> {selectedUser.first_name} {selectedUser.last_name}
                        </p>

                        <div className="form-group mb-3">
                            <label className="mb-1">Select field to edit</label>
                            <Select
                                value={!selectedEditField ? "Select Field" : selectedEditField}
                                onChange={(value) => setSelectedEditField(value)}
                                placeholder="Select Field"
                                style={{ width: "100%" }}
                                options={[
                                    { value: "first_name", label: "First Name" },
                                    { value: "last_name", label: "Last Name" },
                                    { value: "email", label: "Email" },
                                    { value: "role", label: "Role" },
                                    { value: "phone_number", label: "Phone Number" },
                                ]}
                            />
                        </div>

                        {selectedEditField && (
                            <>
                                <div className="form-group mb-3">
                                    <label>Current Value</label>
                                    <input type="text" className="form-control" value={selectedUser[selectedEditField] || ""} disabled />
                                </div>

                                {selectedEditField === "role" ? (
                                    <div className="form-group mb-3">
                                        <label>New Value</label>
                                        <Select
                                            value={!newValue ? "Select Role" : newValue}
                                            onChange={(value) => setNewValue(value)}
                                            placeholder="Select Role"
                                            style={{ width: "100%" }}
                                            options={[
                                                { value: "Learner", label: "Learner" },
                                                { value: "Instructor", label: "Instructor" },
                                            ]}
                                        />
                                    </div>
                                ) : (
                                    <div className="form-group mb-3">
                                        <label>New Value</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={newValue}
                                            onChange={(e) => setNewValue(e.target.value)}
                                        />
                                    </div>
                                )}

                                <div className="d-flex justify-content-center">
                                    <button className="btn primary-btn" onClick={handleSaveChanges} disabled={!newValue.trim()}>
                                        {editUserLoading ? <ImSpinner2 className="btn-spinner" /> : "Save Changes"}
                                    </button>
                                </div>
                            </>
                        )}
                    </>
                )}
            </Modal>

            {/* export CSV modal */}
            <Modal
                centered
                title={<div style={{ fontSize: "large" }} className="d-flex justify-content-center">Select Export Date Range</div>}
                open={filterModalVisible}
                onCancel={() => setFilterModalVisible(false)}
                footer={null}
            >
                <div className="d-flex flex-column gap-4 mb02">
                    <Space className="mt-2" vertical>
                        <DatePicker placeholder="Select Start Date" value={startDate} onChange={(date) => setStartDate(date)} />
                        <DatePicker placeholder="Select End Date" value={endDate} onChange={(date) => setEndDate(date)} />
                    </Space>
                    <button className="btn primary-btn" onClick={exportToCSV} disabled={exportLoading}>
                        {exportLoading ? <ImSpinner2 className="btn-spinner" /> : "Export to CSV"}
                    </button>
                </div>
            </Modal>

            {/* page content */}
           
                <Row className="marginRightNone">
                    <Col xs={24}>
                        <Card style={{ boxShadow: "0 2px 12px rgba(0,0,0,0.08)", border: "none" }}>
                            <div className="mb-3 user-management-header gap-3">
                                <div className="py-3 gap-3 icon-n-title">
                                    <div className="rounded">
                                        <UserOutlined style={{ fontSize: "24px" }} />
                                    </div>
                                    <Typography.Title level={3} style={{ margin: 0, paddingTop: 0 }}>
                                        User Management
                                    </Typography.Title>
                                    <div className="badge rounded-pill">{userCount >= 99 ? "99+" : userCount}</div>
                                </div>

                                <div className="badge-search-btn gap-3">
                                    <div className="gap-3 badge-n-search">
                                        <form role="search">
                                            <div className="search-container">
                                                <img loading="lazy" src="/images/search-icn.png" alt="Search Icon" className="search-icn" />
                                                <input
                                                    type="search"
                                                    className="form-control course-search rounded"
                                                    placeholder="Search users..."
                                                    value={userSearch}
                                                    onChange={(e) => setUserSearch(e.target.value)}
                                                    aria-label="Search"
                                                    style={{ paddingLeft: "40px" }}
                                                />
                                            </div>
                                        </form>
                                    </div>

                                    <div className="d-flex gap-3 align-items-center justify-content-space">
                                        <button
                                            onClick={refreshUsers}
                                            className="btn refresh-btn d-flex align-items-center justify-content-center gap-2"
                                        >
                                            {usersLoading ? (
                                                <ImSpinner2 className="button-spinner-plain" />
                                            ) : (
                                                <>
                                                    Refresh Users <IoIosRefresh />
                                                </>
                                            )}
                                        </button>
                                    </div>

                                    <div>
                                        <button className="secondary-btn btn" onClick={() => setFilterModalVisible(true)}>
                                            Export to CSV
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {filteredUsers.length > 0 ? (
                                <div>
                                    {isUserDataLoading ? (
                                        <div className="users-spinner-container">
                                            <ImSpinner2 className="users-spinner" />
                                        </div>
                                    ) : isMobile ? (
                                        <>
                                            <Row gutter={12} style={{ marginBottom: "1rem" }}>
                                                <Col span={12}>
                                                    <Select value={userSortField} onChange={setUserSortField} style={{ width: "100%" }}>
                                                        <Option value="user_id">User ID</Option>
                                                        <Option value="first_name">First Name</Option>
                                                        <Option value="last_name">Last Name</Option>
                                                        <Option value="email">Email</Option>
                                                        <Option value="role">Role</Option>
                                                        <Option value="creation_date">Creation Date</Option>
                                                        <Option value="is_approved">Approval Status</Option>
                                                    </Select>
                                                </Col>
                                                <Col span={12}>
                                                    <Select value={userSortOrder} onChange={setUserSortOrder} style={{ width: "100%" }}>
                                                        <Option value="asc">Ascending</Option>
                                                        <Option value="desc">Descending</Option>
                                                    </Select>
                                                </Col>
                                            </Row>

                                            {sortedUsers.map((user) => (
                                                <Card
                                                    key={user.user_id}
                                                    className="mb-3 shadow-sm"
                                                    title={
                                                        <div className="d-flex justify-content-between align-items-center">
                                                            <span>{user.first_name} {user.last_name}</span>
                                                            <span className="badge">{user.role}</span>
                                                        </div>
                                                    }
                                                >
                                                    <p><strong>User ID:</strong> {user.user_id}</p>
                                                    <p><strong>Email:</strong> {user.email}</p>
                                                    <p><strong>Role:</strong> {user.role}</p>
                                                    <p><strong>Phone:</strong> {user.phone_number || "-"}</p>
                                                    <p><strong>Created:</strong> {new Date(user.creation_date).toLocaleDateString()}</p>
                                                    <p><strong>Approval Status:</strong></p>
                                                    <div className="mb-3">
                                                        <button
                                                            disabled={user.is_approved === 1 || user.is_approved === null}
                                                            onClick={() => {
                                                                setSelectedUser(user);
                                                                setShowApprovalModal(true);
                                                            }}
                                                            className={
                                                                user.is_approved === 1 || user.is_approved === null
                                                                    ? "approved-badge"
                                                                    : user.is_approved === 2
                                                                        ? "rejected-badge"
                                                                        : "pending-badge"
                                                            }
                                                        >
                                                            {user.is_approved === 1 || user.is_approved === null
                                                                ? "Approved"
                                                                : user.is_approved === 2
                                                                    ? "Rejected"
                                                                    : "Pending Approval"}
                                                        </button>
                                                    </div>
                                                    <div>
                                                        <p><strong>Actions:</strong></p>
                                                        <div className="d-flex gap-2">
                                                            <button
                                                                className="btn btn-sm approve-btn"
                                                                onClick={() => {
                                                                    setShowEditModal(true);
                                                                    setSelectedUser(user);
                                                                }}
                                                            >
                                                                Edit
                                                            </button>
                                                            <button
                                                                disabled={userDetails.user_id === user.user_id}
                                                                className="btn btn-sm danger-btn"
                                                                onClick={() => {
                                                                    setShowUserArchiveModal(true);
                                                                    setSelectedUser(user);
                                                                }}
                                                            >
                                                                Archive User
                                                            </button>
                                                        </div>
                                                    </div>
                                                </Card>
                                            ))}
                                        </>
                                    ) : (
                                        <Table
                                            rowKey="user_id"
                                            dataSource={filteredUsers}
                                            columns={userManagementColumns}
                                            pagination={false}
                                            className="table-header"
                                            loading ={{
                                                spinning: isUserDataLoading,
                                                indicator: <ImSpinner2 className="button-spinner-plain" />,

                                            }}
                                        />
                                    )}

                                    <div style={{ textAlign: "left", marginTop: "20px" }}>
                                        <Pagination
                                            simple
                                            current={currentUserPage}
                                            pageSize={userItemsPerPage}
                                            total={userCount}
                                            onChange={paginateUsers}
                                            style={{
                                                display: "inline-block",
                                                padding: "8px 12px",
                                                borderRadius: "8px",
                                                backgroundColor: "#f0f2f5",
                                                boxShadow: "0 1px 5px rgba(0, 0, 0, 0.1)",
                                                fontWeight: 500,
                                            }}
                                            itemRender={(page, type) => {
                                                if (type === "prev") {
                                                    return <span style={{ color: "#ffa103" }}><CiCircleChevLeft size={27} /></span>;
                                                }
                                                if (type === "next") {
                                                    return <span style={{ color: "#ffa103" }}><CiCircleChevRight size={27} /></span>;
                                                }
                                                return <span style={{ color: "#000" }}>{page}</span>;
                                            }}
                                        />
                                    </div>
                                </div>
                            ) : (
                                <Empty description="No users found" />
                            )}
                        </Card>
                    </Col>
                </Row>
         

        </div>
    );
};

export default UserManagement;